import { useState, useEffect, useRef } from "react";

// Shared Apple/iOS-inspired design tokens, used by the marketing landing
// page, the trial signup page, and the onboarding flow so all three feel
// like one continuous, considered experience rather than three different
// products stitched together.
export const C = {
  bg:        "#ffffff",
  bgAlt:     "#f5f5f7",   // Apple's classic light-grey section bg
  bgDark:    "#000000",
  bgDarkAlt: "#1d1d1f",
  text:      "#1d1d1f",   // Apple near-black
  textSub:   "#6e6e73",   // Apple secondary grey
  textFaint: "#a1a1a6",
  textOnDark:    "#f5f5f7",
  textSubOnDark: "#a1a1a6",
  accent:      "#0071e3", // Apple blue
  accentHover: "#0077ED",
  accentSoft:  "#f0f6ff",
  border:      "#d2d2d7",
  borderSoft:  "#e8e8ed",
  borderDark:  "#38383d",
  red:         "#e0393e",
  redSoft:     "#fdf1f1",
  green:       "#1e8e3e",
  greenSoft:   "#f0f9f2",
  amber:       "#b7791f",
  amberSoft:   "#fdf6ec",
  // Added for the authenticated-app redesign (Dashboard etc.) — additive
  // only, doesn't touch any existing key/value used by the marketing pages.
  purple:      "#5856d6",
  purpleSoft:  "#f1f1fd",
  cyan:        "#0a84a8",
  cyanSoft:    "#eaf7fa",

  // Phase 1 design-system foundation (full app redesign, see
  // linear-brewing-toast.md) — semantic aliases so every screen still on
  // App.js's local Fluent `T` palette (accent:"#0078D4" etc.) or
  // workspaceTheme.js's WORKSPACE_THEME can be migrated onto this one C
  // object with a plain token-name swap, no value remapping. Every key
  // below is NEW — nothing above is renamed or removed, so nothing that
  // already reads C.bg/C.accent/C.purpleSoft/etc. is affected.
  bgCard:      "#ffffff",  // card/panel surface (same value as bg — distinct name for T.bgCard call sites)
  bgHover:     "#f5f5f7",  // row/item hover tint (same value as bgAlt)
  bgDeep:      "#ececef",  // nested panel / stat-pill fill, one step deeper than bgAlt
  borderHi:    "#c7c7cc",  // emphasis border/divider, one step darker than border
  accentHi:    "#0077ED",  // hover/pressed accent (same value as accentHover)
  accentMuted: "#f0f6ff",  // tinted accent fill (same value as accentSoft)
  textMuted:   "#a1a1a6",  // de-emphasized text (same value as textFaint)
  greenMuted:  "#f0f9f2",  // same value as greenSoft
  amberMuted:  "#fdf6ec",  // same value as amberSoft
  redMuted:    "#fdf1f1",  // same value as redSoft
  purpleMuted: "#f1f1fd",  // same value as purpleSoft
  cyanMuted:   "#eaf7fa",  // same value as cyanSoft
};

export const FONT = `-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Segoe UI", Helvetica, Arial, sans-serif`;

// Small reveal-on-scroll wrapper — content quietly fades/rises into place
// as it enters the viewport, rather than everything animating at once.
export function Reveal({ children, delay = 0, style }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); io.disconnect(); }
    }, { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(20px)",
      transition: `opacity .6s cubic-bezier(.22,.61,.36,1) ${delay}s, transform .6s cubic-bezier(.22,.61,.36,1) ${delay}s`,
      ...style,
    }}>
      {children}
    </div>
  );
}
